import './hub.css';
import { GAMES } from './games.js';
import { artFor } from './art.js';
import { SAVE_MIRROR_KEY, TOTAL_LEVELS } from '../config/SiteConstants.js';

// The hub renders the catalog from GAMES and nothing else. Its only game-side
// import is SiteConstants — two plain values, no machinery: pulling GameConfig
// would drag in PlayerConfig's jump simulator and MathUtil, and pulling
// SaveSystem would drag in the whole save engine. So this page never touches
// Three.js and paints instantly on a cold cache.

// ---- Save progress -----------------------------------------------------------
// Read-only peek at the localStorage mirror SaveSystem keeps alongside its
// IndexedDB primary. We never write here: the hub must not be able to corrupt
// a save, and a hub that opened the real DB would race the game's own writes.
function readSkylineProgress() {
  try {
    const raw = localStorage.getItem(SAVE_MIRROR_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw)?.progress;
    if (!p || typeof p.unlockedLevel !== 'number') return null;
    // A save is attacker-editable local data, so treat every field as hostile:
    // clamp to the real level range and ignore anything non-finite.
    const total = TOTAL_LEVELS;
    const lvl = (v) => (Number.isFinite(v) ? Math.min(total, Math.max(1, Math.floor(v))) : 1);
    const completed = p.completed;
    const done = completed && typeof completed === 'object'
      ? Math.min(total, Object.keys(completed).length)
      : 0;
    return {
      done,
      unlocked: lvl(p.unlockedLevel),
      last: lvl(p.lastPlayedLevel ?? p.unlockedLevel),
      total,
    };
  } catch {
    return null; // private-mode or wiped storage — just render the card clean
  }
}

const PLAY_ICON =
  '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 1 L11 6 L2 11 Z" fill="currentColor"/></svg>';

function progressStrip(kind) {
  if (kind !== 'skyline') return '';
  const p = readSkylineProgress();
  if (!p) {
    return `<div class="prog"><div class="prog-meta"><span>New game</span>
      <span><b>${TOTAL_LEVELS.toLocaleString()}</b> levels</span></div></div>`;
  }
  const pct = Math.min(100, (p.done / p.total) * 100);
  return `<div class="prog">
    <div class="prog-bar"><i style="width:${pct.toFixed(2)}%"></i></div>
    <div class="prog-meta">
      <span><b>${p.done.toLocaleString()}</b> of ${p.total.toLocaleString()} cleared</span>
      <span>Resume level <b>${p.last.toLocaleString()}</b></span>
    </div>
  </div>`;
}

function cardHTML(g) {
  const live = g.status === 'playable';
  const badge = live
    ? '<span class="badge live"><span class="dot"></span>Playable</span>'
    : '<span class="badge soon">Placeholder</span>';
  const play = live
    ? `<div class="play">${PLAY_ICON}<span>Play ${g.title}</span></div>`
    : '';

  return `
    <div class="shot">${artFor(g.art)}${badge}${play}</div>
    <div class="body">
      <div class="row">
        <h3>${g.title}</h3>
        <span class="genre">${g.genre}</span>
      </div>
      <p>${g.tagline}</p>
      ${progressStrip(g.progress)}
    </div>`;
}

function render(root) {
  const playable = GAMES.filter((g) => g.status === 'playable');
  const upcoming = GAMES.filter((g) => g.status !== 'playable');

  const grid = document.createElement('div');
  grid.className = 'grid';

  for (const g of GAMES) {
    // A playable game is an <a> (real link, keyboard-focusable, middle-clickable);
    // an unbuilt one is a <div> so it is neither focusable nor clickable.
    const isLive = g.status === 'playable';
    const card = document.createElement(isLive ? 'a' : 'div');
    card.className = `card ${isLive ? '' : 'soon'}`.trim();
    card.style.setProperty('--tint', g.accent ?? 'var(--accent)');
    if (isLive) {
      card.href = g.href;
      card.setAttribute('aria-label', `Play ${g.title} — ${g.genre}`);
    } else {
      card.setAttribute('aria-disabled', 'true');
    }
    card.innerHTML = cardHTML(g);
    grid.append(card);
  }

  root.innerHTML = `
    <header class="mast">
      <div class="wrap">
        <h1 class="wordmark">Park Gentlemen</h1>
        <div class="tag">An arcade of very unsafe playgrounds</div>
        <p class="lede">A small collection of browser games. No installs, no accounts —
          progress saves to this browser. Pick something and go.</p>
      </div>
    </header>
    <main class="wrap">
      <div class="sec-head">
        <h2>The Arcade</h2>
        <span class="rule"></span>
        <span class="count">${playable.length} playable · ${upcoming.length} placeholder${upcoming.length === 1 ? '' : 's'}</span>
      </div>
      <div id="grid-slot"></div>
    </main>
    <footer class="foot">
      <div class="wrap">
        <span>Built with Three.js · runs on any modern browser</span>
        <span>Progress is stored locally — export a save from inside any game.</span>
      </div>
    </footer>`;

  root.querySelector('#grid-slot').replaceWith(grid);
}

render(document.getElementById('hub'));
